import { captureAttribution } from "./attribution";
import {
  readStored,
  readStoredJson,
  writeStored,
  writeStoredJson,
} from "./storage";
import { request } from "./transport";
import type {
  SnipcatConfig,
  SnipcatLeadInput,
  SnipcatLeadResult,
  SnipcatPageStat,
  SnipcatPageStats,
  SnipcatPatch,
  SnipcatPlatform,
} from "./types";
import { eventKey, mergePatches, sanitizeValues } from "./values";

/**
 * One funnel session per visit.
 *
 * The session hash lives in localStorage and a reload MUST resume the same
 * session: reloading is routine in in-app webviews, and a second session there
 * would split one visit into two half-recorded ones - exactly what the
 * drop-off numbers must not contain.
 *
 * Everything produced before the hash is known waits in an outbox that is
 * merged into a single PATCH. The outbox is capped and gives up after repeated
 * failures, so telemetry can never grow the heap with the number of events nor
 * hammer an endpoint that is down.
 */

/** Prefix of every localStorage key the SDK owns. */
export const DEFAULT_STORAGE_PREFIX = "snipcat_";

const DEFAULTS = {
  apiBase: "https://app.snipcat.ai/api",
  storagePrefix: DEFAULT_STORAGE_PREFIX,
  autoStart: true,
  autoTrack: true,
  debug: false,
};

/**
 * Storage key for one SESSION's state - its hash and its page timings.
 *
 * Scoped by formHash, and that scoping is what keeps ad attribution alive
 * across a multi-page site. A session is anchored server-side to the form of
 * the page that created it, so a visitor who arrived from an ad on one page
 * and converted on another would otherwise hand the embedded form a session
 * belonging to a DIFFERENT form. The server refuses to adopt that one, and the
 * submit would start a fresh visit that never saw the ad parameters -
 * silently, because an unattributed lead looks exactly like an organic one.
 *
 * The extra session costs nothing that matters: ad parameters are stored
 * SEPARATELY (attribution.ts, keyed `attr_*` and deliberately NOT scoped here)
 * and re-sent on every create, so each per-form session carries the full click
 * that paid for the visit. The real cost is one attribution ROW per form the
 * visitor touched instead of one per visit - equal values, separate ids.
 *
 * With no formHash the key is unscoped, so an embed without one behaves
 * exactly as it did before.
 *
 * Exported because the page that CREATES the session (the form view creates it
 * itself, see FormViewPage) has to offer the stored hash back so the server can
 * resume that visit instead of starting a second one after every reload.
 */
export const sessionStorageKey = (
  name: string,
  formHash?: string | null,
  prefix: string = DEFAULT_STORAGE_PREFIX,
): string => prefix + name + (formHash ? "_" + formHash : "");

/**
 * The session this browser last used for `formHash`, if any. A reload MUST
 * resume the same visit - reloading is routine in in-app webviews, and a second
 * session there splits one visit into two half-recorded ones - so the page that
 * creates the session offers this hash back and the server adopts it.
 */
export const readStoredSessionHash = (
  formHash?: string | null,
  prefix: string = DEFAULT_STORAGE_PREFIX,
): string | null => readStored(sessionStorageKey("session", formHash, prefix));

/** Patches produced within this window go out as ONE request. */
const FLUSH_DELAY_MS = 300;
/** Session creation: the first attempt plus one retry per delay listed here. */
const CREATE_RETRY_DELAYS_MS = [3000, 15000];
/** Consecutive failed PATCHes after which tracking gives up for this page. */
const MAX_PATCH_FAILURES = 5;
/** Queued patches kept at most; the oldest are dropped once the cap is hit. */
const OUTBOX_MAX = 100;

/**
 * Time on a step is sent as a HEARTBEAT while the page is visible, not only
 * when it is left: on phones and in in-app browsers the tab is routinely
 * killed without any pagehide/visibilitychange, and a visit that only reports
 * on leave then keeps the first, short segment and loses the long read after
 * it - every long visit looks like a 30-second one. The cumulative total goes
 * out every time, so a lost last heartbeat costs one interval, not the visit.
 *
 * Dense while the interesting part lasts, sparse after: every 10 s during the
 * first minute (counted time), every 30 s to five minutes, every minute to
 * the cap. At the cap the step stops being timed - a tab left open is not
 * time on the page.
 */
const HEARTBEAT_CAP_MS = 10 * 60_000;
const heartbeatDelayMs = (countedMs: number): number => {
  if (countedMs < 60_000) {
    return 10_000;
  }
  if (countedMs < 5 * 60_000) {
    return 30_000;
  }
  return 60_000;
};
/**
 * Without any input for this long the visitor is taken to have walked away:
 * the segment is closed at the moment the idle period began and timing only
 * resumes with the next input. Five minutes is long enough to read a screen
 * of text without touching anything.
 */
const IDLE_MS = 5 * 60_000;
/** Input events that count as "still here". Passive and capture: never in the page's way. */
const ACTIVITY_EVENTS = [
  "pointerdown",
  "pointermove",
  "keydown",
  "wheel",
  "scroll",
  "touchstart",
];

export class SnipcatClient {
  private config: Required<
    Pick<
      SnipcatConfig,
      "apiBase" | "storagePrefix" | "autoStart" | "autoTrack" | "debug"
    >
  > &
    SnipcatConfig = { ...DEFAULTS };

  private sessionHash: string | null = null;
  private sessionPromise: Promise<string | null> | null = null;
  private outbox: SnipcatPatch[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private patchFailures = 0;
  /** Set once tracking gave up - nothing is queued or sent afterwards. */
  private disabled = false;
  private started = false;
  private listenersAttached = false;

  private pageStats: SnipcatPageStats | null = null;
  /**
   * Step being timed: `since` = start of the running segment, `counted` = ms
   * of it already counted in THIS page load (the cap applies to that, so a
   * reload starts a fresh ten minutes), `idle` = waiting for input.
   */
  private currentStep: {
    id: string;
    since: number;
    counted: number;
    idle: boolean;
  } | null = null;
  private heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
  private lastActivity = 0;

  // ── lifecycle ──────────────────────────────────────────────────────────

  init(config: SnipcatConfig): SnipcatClient {
    this.config = {
      ...DEFAULTS,
      ...config,
      apiBase: (config.apiBase || DEFAULTS.apiBase).replace(/\/+$/, ""),
    };
    this.started = true;
    // The ad parameters are read on EVERY init, not only when a session is
    // created: a visitor who lands on the ad URL, browses on and converts two
    // pages later must keep the click that paid for the visit.
    captureAttribution(this.config.storagePrefix);
    // BEFORE the listeners and before autoStart: an auto-tracked page view
    // fires in this very tick, and queue() starts a session of its own if
    // there is none - which is the second session this option exists to
    // prevent.
    if (config.sessionHash) {
      this.adoptSession(config.sessionHash);
    }
    this.attachListeners();
    if (this.config.autoStart) {
      void this.ensureSession();
    }
    return this;
  }

  /** True once init() ran - the guard every public method starts with. */
  private ready(): boolean {
    if (!this.started && this.config.debug) {
      // eslint-disable-next-line no-console
      console.warn("[snipcat] called before init() - ignored");
    }
    return this.started;
  }

  private key(name: string): string {
    return sessionStorageKey(
      name,
      this.config.formHash,
      this.config.storagePrefix,
    );
  }

  // ── session ────────────────────────────────────────────────────────────

  /**
   * Resolves with the hash of this visit's session: the stored one when there
   * is any, a newly created one otherwise. Safe to call from anywhere - the
   * session is created at most once.
   */
  ensureSession(): Promise<string | null> {
    if (!this.ready()) {
      return Promise.resolve(null);
    }
    if (this.sessionPromise) {
      return this.sessionPromise;
    }
    const stored = readStored(this.key("session"));
    if (stored) {
      this.sessionHash = stored;
      this.scheduleFlush(0);
      this.sessionPromise = Promise.resolve(stored);
      return this.sessionPromise;
    }
    // A session is about to be created, so leftover timings belong to an older
    // one - dropped now, before the first step of this visit is timed.
    writeStored(this.key("pageStats"), null);
    this.pageStats = {};

    this.sessionPromise = this.createWithRetry().then((hash) => {
      if (!hash) {
        // Nowhere to deliver, so drop what is queued instead of holding on to
        // it for the rest of the visit.
        this.disabled = true;
        this.outbox = [];
        return null;
      }
      // Assigned synchronously here so an event fired in this very tick lands
      // in this session.
      this.sessionHash = hash;
      writeStored(this.key("session"), hash);
      this.scheduleFlush(0);
      return hash;
    });
    return this.sessionPromise;
  }

  /**
   * Take over a session created OUTSIDE the SDK instead of starting one.
   *
   * The form view is the case: it creates the visit itself, in one call that
   * also anchors it to the form and adopts an embedding host's session
   * (POST /f/<formHash>/session), and then hands the hash here so the tracking
   * writes into that same visit. Without this the page had two sessions - the
   * form's and the SDK's - for one visitor, split down the middle: the answers
   * and the submit on one, the scroll depth, the device and the time on page on
   * the other.
   *
   * Usually passed as `sessionHash` in init(); public because a page that gets
   * its session later (consent, a lazy create) can hand it over afterwards.
   */
  adoptSession(sessionHash: string): void {
    if (!sessionHash || this.sessionHash === sessionHash) {
      return;
    }
    // Timings of a DIFFERENT session would be merged into this one as if the
    // visitor had already been through those steps.
    if (readStored(this.key("session")) !== sessionHash) {
      writeStored(this.key("pageStats"), null);
      this.pageStats = {};
    }
    this.sessionHash = sessionHash;
    this.sessionPromise = Promise.resolve(sessionHash);
    writeStored(this.key("session"), sessionHash);
    this.scheduleFlush(0);
  }

  private async createWithRetry(): Promise<string | null> {
    const attribution = captureAttribution(this.config.storagePrefix);
    for (let attempt = 0; attempt <= CREATE_RETRY_DELAYS_MS.length; attempt++) {
      if (attempt > 0) {
        await new Promise((resolve) =>
          setTimeout(resolve, CREATE_RETRY_DELAYS_MS[attempt - 1]),
        );
      }
      const response = await request(
        this.config.apiBase + "/funnel/session",
        "POST",
        {
          ...(this.config.formHash ? { formHash: this.config.formHash } : {}),
          // its own section, never spread across the body: where the visit came from, what the
          // visitor types and how they move through pages are three different things, stored in
          // three different places
          attribution,
        },
        { debug: this.config.debug },
      );
      const hash = response.ok ? response.data?.sessionHash : null;
      if (typeof hash === "string" && hash) {
        return hash;
      }
      // A 4xx is the server saying "never" (no default workspace configured,
      // unknown formHash) - retrying it only wastes the visitor's battery.
      if (response.status >= 400 && response.status < 500) {
        return null;
      }
    }
    return null;
  }

  /** The hash of this visit, or null while it is still being created. */
  getSessionHash(): string | null {
    return this.sessionHash;
  }

  /** Forgets this visit. The next track() starts a brand new session. */
  reset(): void {
    this.sessionHash = null;
    this.sessionPromise = null;
    this.outbox = [];
    this.pageStats = {};
    this.currentStep = null;
    this.stopHeartbeat();
    this.disabled = false;
    this.patchFailures = 0;
    writeStored(this.key("session"), null);
    writeStored(this.key("pageStats"), null);
  }

  // ── tracking ───────────────────────────────────────────────────────────

  /** Queues flat values for the session of this visit. */
  track(values: Record<string, unknown>): void {
    if (!this.ready() || this.disabled) {
      return;
    }
    const clean = sanitizeValues(values);
    if (!Object.keys(clean).length) {
      return;
    }
    this.queue({ values: clean });
  }

  /**
   * Records that something happened. `values` is flat, so the variant becomes
   * part of the key and the value is the moment it happened (or the number the
   * caller passed).
   */
  trackEvent(
    event: string,
    variant?: string | null,
    value?: string | number,
  ): void {
    if (!event) {
      return;
    }
    this.track({
      [eventKey(event, variant)]: value ?? new Date().toISOString(),
    });
  }

  private queue(patch: SnipcatPatch): void {
    this.outbox = [...this.outbox, patch].slice(-OUTBOX_MAX);
    // Lazily start the session on the first thing worth recording, so a
    // visitor who never interacts never enters the funnel numbers at all.
    void this.ensureSession();
    this.scheduleFlush(FLUSH_DELAY_MS);
  }

  private scheduleFlush(delayMs: number): void {
    if (this.disabled || !this.outbox.length || this.flushTimer !== null) {
      return;
    }
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flush();
    }, delayMs);
  }

  /** Sends everything queued as a single PATCH. */
  flush(keepalive = false): void {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (this.disabled || !this.sessionHash || !this.outbox.length) {
      return;
    }
    const body = mergePatches(this.outbox);
    this.outbox = [];
    if (!Object.keys(body).length) {
      return;
    }
    void request(
      this.config.apiBase + "/funnel/session/" + this.sessionHash,
      "PATCH",
      body,
      { keepalive, debug: this.config.debug },
    ).then((response) => {
      if (response.ok) {
        this.patchFailures = 0;
        return;
      }
      // 400 = the visit is completed (a lead was submitted) or the body is
      // refused; 404 = the session is gone. Retrying either is pointless and
      // would loop until the failure cap.
      if (response.status === 400 || response.status === 404) {
        this.disabled = true;
        this.outbox = [];
        return;
      }
      this.patchFailures++;
      if (this.patchFailures >= MAX_PATCH_FAILURES) {
        this.disabled = true;
        this.outbox = [];
        return;
      }
      // Put the merged body back as ONE item: newer patches keep overriding
      // it, so a failing server costs neither memory nor extra requests.
      this.outbox = [body, ...this.outbox].slice(0, OUTBOX_MAX);
    });
  }

  // ── step timing ────────────────────────────────────────────────────────

  /**
   * Closes the timing of the previous step and starts timing `stepId`.
   * Pass null to stop timing entirely.
   *
   * Timings are cumulative and kept client side (localStorage, so a reload
   * continues where it left off) because the server merges pageStats SHALLOW:
   * a step entered twice must be sent with its TOTAL time, or the second visit
   * overwrites the first instead of adding to it.
   */
  trackStep(stepId: string | null): void {
    if (!this.ready()) {
      return;
    }
    const now = Date.now();
    this.closeStepSegment(now);
    this.stopHeartbeat();
    this.currentStep = stepId
      ? { id: stepId, since: now, counted: 0, idle: false }
      : null;
    if (stepId) {
      this.lastActivity = now;
      this.scheduleHeartbeat();
      void this.ensureSession();
    }
  }

  // ── heartbeat ──────────────────────────────────────────────────────────

  private scheduleHeartbeat(): void {
    if (
      this.heartbeatTimer !== null ||
      !this.currentStep ||
      this.currentStep.idle ||
      this.currentStep.counted >= HEARTBEAT_CAP_MS
    ) {
      return;
    }
    this.heartbeatTimer = setTimeout(
      () => this.heartbeat(),
      heartbeatDelayMs(this.currentStep.counted),
    );
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearTimeout(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private heartbeat(): void {
    this.heartbeatTimer = null;
    if (!this.currentStep) {
      return;
    }
    if (
      typeof document !== "undefined" &&
      document.visibilityState === "hidden"
    ) {
      // the leave handler already closed the segment; visible again reopens it
      return;
    }
    const now = Date.now();
    const idleSince = this.lastActivity + IDLE_MS;
    if (now >= idleSince) {
      // count up to the moment the visitor went quiet, then wait for input
      this.closeStepSegment(idleSince);
      this.currentStep.idle = true;
      return;
    }
    this.closeStepSegment(now);
    this.scheduleHeartbeat();
  }

  /** Any input: the visitor is still here. Wakes an idle step. */
  private noteActivity(): void {
    const now = Date.now();
    this.lastActivity = now;
    if (this.currentStep && this.currentStep.idle) {
      // the quiet gap is not time on the step
      this.currentStep.since = now;
      this.currentStep.idle = false;
    }
    this.scheduleHeartbeat();
  }

  private readPageStats(): SnipcatPageStats {
    if (!this.pageStats) {
      this.pageStats = readStoredJson<SnipcatPageStats>(
        this.key("pageStats"),
        {},
      );
    }
    return this.pageStats;
  }

  private closeStepSegment(now: number): void {
    if (!this.currentStep) {
      return;
    }
    const stats = this.readPageStats();
    const previous = stats[this.currentStep.id];
    // `now` may lie before `since` (idle moment computed from an older input
    // than the segment start) - then there is simply nothing to count.
    const elapsedMs = Math.min(
      Math.max(0, now - this.currentStep.since),
      Math.max(0, HEARTBEAT_CAP_MS - this.currentStep.counted),
    );
    if (!elapsedMs && previous) {
      // already closed at this very moment - pagehide and visibilitychange
      // both fire when a tab goes away, and the timing must not be sent twice
      return;
    }
    const record: SnipcatPageStat = {
      // the FIRST entry into the step, kept across later visits to it
      enteredAt:
        previous?.enteredAt ?? new Date(this.currentStep.since).toISOString(),
      leftAt: new Date(now).toISOString(),
      timeSpentMs: (previous?.timeSpentMs ?? 0) + elapsedMs,
    };
    stats[this.currentStep.id] = record;
    writeStoredJson(this.key("pageStats"), stats);
    // the segment is closed, so a later close must not count its time again
    this.currentStep = {
      ...this.currentStep,
      since: Math.max(now, this.currentStep.since),
      counted: this.currentStep.counted + elapsedMs,
    };
    this.queue({ pageStats: { [this.currentStep.id]: record } });
  }

  // ── lead ───────────────────────────────────────────────────────────────

  /**
   * Submits the visitor's details as a LEAD and links it to this visit.
   *
   * The session hash travels with the submit, so the lead inherits the very
   * `attribution` row the visit created - "this customer came from that ad"
   * stays a matching id instead of a guess from equal utm strings. The visit
   * is completed by the server afterwards: one visit produces one lead.
   */
  async submitLead(input: SnipcatLeadInput): Promise<SnipcatLeadResult> {
    if (!this.ready()) {
      return { ok: false, lead: null, status: 0 };
    }
    if (!this.config.leadHash) {
      if (this.config.debug) {
        // eslint-disable-next-line no-console
        console.warn("[snipcat] submitLead() needs a leadHash in init()");
      }
      return { ok: false, lead: null, status: 0 };
    }
    // Wait for the session so the lead can name it, but never let a failed
    // session block the submit - the lead is the thing worth keeping.
    const sessionHash = await this.ensureSession().catch(() => null);
    // Everything queued belongs to the visit that produced this lead, and the
    // server refuses a PATCH once the visit is completed - so it goes now.
    this.closeStepSegment(Date.now());
    this.flush();

    const response = await request(
      this.config.apiBase + "/lead/lead-public/" + this.config.leadHash,
      "POST",
      {
        ...(input.name ? { name: input.name } : {}),
        ...(input.email ? { email: input.email } : {}),
        ...(input.phone ? { phone: input.phone } : {}),
        ...(input.customFieldValues
          ? { customFieldValues: input.customFieldValues }
          : {}),
        ...(sessionHash ? { funnelSessionHash: sessionHash } : {}),
      },
      { debug: this.config.debug },
    );

    if (response.ok) {
      // The visit is spent server side; keep the client from PATCHing into a
      // completed session (which would 400 and disable tracking anyway).
      this.disabled = true;
      this.outbox = [];
      this.currentStep = null;
    }
    return {
      ok: response.ok,
      lead: response.ok ? response.data : null,
      status: response.status,
    };
  }

  /**
   * Stamps a store-button click on this visit so the app's first launch can
   * match the install back to it (web -> app install continuity).
   */
  async claim(platform: SnipcatPlatform): Promise<boolean> {
    if (!this.ready()) {
      return false;
    }
    const sessionHash = await this.ensureSession();
    if (!sessionHash) {
      return false;
    }
    const response = await request(
      this.config.apiBase + "/funnel/session/" + sessionHash + "/claim",
      "POST",
      { platform },
      { debug: this.config.debug },
    );
    return response.ok;
  }

  // ── page lifecycle ─────────────────────────────────────────────────────

  private attachListeners(): void {
    if (this.listenersAttached || typeof window === "undefined") {
      return;
    }
    this.listenersAttached = true;

    const leave = (): void => {
      // The visitor leaving mid-step IS the drop-off pageStats exists for, so
      // the timing has to go out now - nothing else would ever send it.
      this.closeStepSegment(Date.now());
      this.flush(true);
    };

    // pagehide is the only reliable "the page is going away" signal in mobile
    // browsers and in-app webviews; visibilitychange additionally covers
    // switching away from the app, after which the tab is often killed
    // without any further event.
    window.addEventListener("pagehide", leave);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        leave();
        return;
      }
      // back from the background: time spent there is not time on the step
      if (this.currentStep) {
        const now = Date.now();
        this.currentStep = { ...this.currentStep, since: now, idle: false };
        this.lastActivity = now;
        this.scheduleHeartbeat();
      }
    });
    ACTIVITY_EVENTS.forEach((name) => {
      window.addEventListener(name, () => this.noteActivity(), {
        capture: true,
        passive: true,
      });
    });
  }

  /** Read-only view of the effective configuration (used by the auto-tracker). */
  getConfig(): SnipcatConfig {
    return this.config;
  }
}
